import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  DollarSign, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  ToggleLeft,
  ToggleRight,
  Package,
  Truck,
  Shield,
  AlertCircle
} from 'lucide-react';

const CheckoutCostsManagement = () => {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCost, setEditingCost] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    costType: 'FIXED',
    amount: 0,
    minimumOrderAmount: '',
    maximumOrderAmount: '',
    region: '',
    isActive: true,
    sortOrder: 0
  });

  useEffect(() => {
    fetchCosts();
  }, []);

  const fetchCosts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/checkout-costs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCosts(data);
      }
    } catch (error) {
      console.error('Error fetching checkout costs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingCost 
        ? `${API_BASE_URL}/api/admin/checkout-costs/${editingCost.id}`
        : `${API_BASE_URL}/api/admin/checkout-costs`;
      
      const method = editingCost ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          minimumOrderAmount: formData.minimumOrderAmount ? parseFloat(formData.minimumOrderAmount) : null,
          maximumOrderAmount: formData.maximumOrderAmount ? parseFloat(formData.maximumOrderAmount) : null,
          sortOrder: parseInt(formData.sortOrder) || 0
        })
      });

      if (response.ok) {
        fetchCosts();
        resetForm();
        setShowAddModal(false);
        setEditingCost(null);
      }
    } catch (error) {
      console.error('Error saving checkout cost:', error);
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      name: cost.name,
      description: cost.description || '',
      costType: cost.costType,
      amount: cost.amount,
      minimumOrderAmount: cost.minimumOrderAmount || '',
      maximumOrderAmount: cost.maximumOrderAmount || '',
      region: cost.region || '',
      isActive: cost.isActive,
      sortOrder: cost.sortOrder
    });
    setShowAddModal(true);
  };

  const handleDelete = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this cost component?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/checkout-costs/${costId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchCosts();
      }
    } catch (error) {
      console.error('Error deleting checkout cost:', error);
    }
  };

  const handleToggleActive = async (cost) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/checkout-costs/${cost.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !cost.isActive })
      });

      if (response.ok) {
        fetchCosts();
      }
    } catch (error) {
      console.error('Error toggling cost status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      costType: 'FIXED',
      amount: 0,
      minimumOrderAmount: '',
      maximumOrderAmount: '',
      region: '',
      isActive: true,
      sortOrder: 0
    });
  };

  const getCostIcon = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('shipping') || nameLower.includes('delivery')) {
      return <Truck className="h-5 w-5" />;
    }
    if (nameLower.includes('handling') || nameLower.includes('processing')) {
      return <Package className="h-5 w-5" />;
    }
    if (nameLower.includes('insurance')) {
      return <Shield className="h-5 w-5" />;
    }
    return <DollarSign className="h-5 w-5" />;
  };

  const calculatePreview = (amount, costType) => {
    if (costType === 'FIXED') {
      return `KSh ${amount.toLocaleString()}`;
    }
    return `${amount}%`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Checkout Costs</h2>
          <p className="text-gray-600 text-sm mt-1">
            Manage additional costs like shipping, handling, and other fees
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingCost(null);
            setShowAddModal(true);
          }}
          className="bg-[#d44100] text-white px-4 py-2 rounded-lg hover:bg-[#b83700] flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Cost
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h3 className="font-semibold text-blue-800">How Checkout Costs Work</h3>
            <p className="text-sm text-blue-700 mt-1">
              These costs will be added to the checkout total. Use FIXED for set amounts (e.g., shipping fee) 
              or PERCENTAGE for percentage-based fees (e.g., handling fee as % of order). 
              VAT has been removed from the default calculation.
            </p>
          </div>
        </div>
      </div>

      {/* Costs Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conditions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costs.map((cost) => (
                <tr key={cost.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cost.sortOrder}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        {getCostIcon(cost.name)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {cost.name}
                        </div>
                        {cost.description && (
                          <div className="text-sm text-gray-500">
                            {cost.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      cost.costType === 'FIXED' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {cost.costType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {calculatePreview(cost.amount, cost.costType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cost.minimumOrderAmount || cost.maximumOrderAmount ? (
                      <div>
                        {cost.minimumOrderAmount && (
                          <span>Min: KSh {cost.minimumOrderAmount.toLocaleString()}</span>
                        )}
                        {cost.minimumOrderAmount && cost.maximumOrderAmount && ' / '}
                        {cost.maximumOrderAmount && (
                          <span>Max: KSh {cost.maximumOrderAmount.toLocaleString()}</span>
                        )}
                      </div>
                    ) : (
                      'No restrictions'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(cost)}
                      className={`flex items-center ${
                        cost.isActive ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      {cost.isActive ? (
                        <ToggleRight className="h-6 w-6" />
                      ) : (
                        <ToggleLeft className="h-6 w-6" />
                      )}
                      <span className="ml-2 text-sm">
                        {cost.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(cost)}
                        className="text-[#d44100] hover:text-[#b83700]"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cost.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {costs.length === 0 && (
          <div className="text-center py-8">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No checkout costs configured</p>
            <p className="text-gray-400 text-sm mt-1">
              Add costs like shipping, handling, or insurance fees
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {editingCost ? 'Edit Cost' : 'Add Checkout Cost'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCost(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      placeholder="e.g., Shipping, Handling Fee"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      placeholder="Optional description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type *
                      </label>
                      <select
                        name="costType"
                        value={formData.costType}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      >
                        <option value="FIXED">Fixed Amount</option>
                        <option value="PERCENTAGE">Percentage</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount *
                      </label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step={formData.costType === 'PERCENTAGE' ? "0.1" : "1"}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                        placeholder={formData.costType === 'PERCENTAGE' ? "e.g., 5" : "e.g., 500"}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Order (KSh)
                      </label>
                      <input
                        type="number"
                        name="minimumOrderAmount"
                        value={formData.minimumOrderAmount}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Order (KSh)
                      </label>
                      <input
                        type="number"
                        name="maximumOrderAmount"
                        value={formData.maximumOrderAmount}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Region Filter
                    </label>
                    <input
                      type="text"
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      placeholder="e.g., Nairobi, Central (leave empty for all)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="sortOrder"
                      value={formData.sortOrder}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-[#d44100] border-gray-300 rounded focus:ring-[#d44100]"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                      Active (include in checkout calculations)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end mt-6 space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingCost(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingCost ? 'Update' : 'Add'} Cost
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutCostsManagement;